import React from 'react';

import {
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  styled,
} from '@mui/material';
import { useField } from 'formik';
import { ArrowDown2 } from 'iconsax-reactjs';
import { useTranslation } from 'react-i18next';

import FormSelectSearch from './components/FormSelectSearch';
import { IFormSelectSearchProps } from './types';
import { IFormSelectProps } from '../../types/control.type';

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.75rem',
  lineHeight: '0.875rem',
  color: '#606F84',
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary[200],
  },
}));

const FormSelect = React.memo(
  ({
    useEmptyValue = false,
    label,
    items,
    onChange,
    controlSx = {},
    gridProps = {},
    useTranslate = false,
    fullWidth = true,
    isDisabled = false,
    useSearch = false,
    listSx = {},
    ...props
  }: IFormSelectProps) => {
    const [search, setSearch] = React.useState('');

    const [field, meta] = useField(props);

    const { t } = useTranslation();

    const itemsFiltered = React.useMemo(() => {
      if (!useSearch || !search.length) {
        return items;
      }

      return items.filter((item) => item.label.toLowerCase().includes(search.toLowerCase()));
    }, [items, search, useSearch]);

    const renderLabel = (value: string) => {
      if (useTranslate) {
        return t(value);
      }

      return value;
    };

    const handleSearchChange = React.useCallback((value: string) => {
      setSearch(value);
    }, []);

    const handleSearchClear = React.useCallback(() => {
      setSearch('');
    }, []);

    const menuProps = React.useMemo(() => {
      if (!useSearch) {
        return {
          slotProps: {
            list: {
              sx: listSx,
            },
          },
        };
      }

      return {
        autoFocus: false,
        slots: {
          paper: FormSelectSearch,
        },
        slotProps: {
          paper: {
            search,
            onSearchChange: handleSearchChange,
            onSearchClear: handleSearchClear,
          } as IFormSelectSearchProps,
          list: {
            sx: listSx,
          },
        },
        sx: {
          '& .MuiList-root': {
            maxHeight: '300px',
          },
        },
      };
    }, [useSearch, search, handleSearchChange, handleSearchClear, listSx]);

    return (
      <Grid {...gridProps}>
        <FormControl fullWidth={fullWidth} sx={{ minWidth: 250, ...controlSx }}>
          <FormLabel {...(meta.touched && Boolean(meta.error) && { color: 'error' })}>
            {label}
          </FormLabel>

          <Select
            {...field}
            {...props}
            error={meta.touched && Boolean(meta.error)}
            data-testid={`select-test-${props.name}`}
            IconComponent={ArrowDown2}
            disabled={isDisabled}
            MenuProps={menuProps}
          >
            {useEmptyValue && (
              <MenuItemStyled value="">
                <em>{t('general.all')}</em>
              </MenuItemStyled>
            )}

            {itemsFiltered.map((item) => (
              <MenuItemStyled key={`${props.name}-${item.value}`} value={item.value}>
                {renderLabel(item.label?.length ? item.label : item.value)}
              </MenuItemStyled>
            ))}
          </Select>

          {meta.touched && meta.error && <FormHelperText error>{meta.error}</FormHelperText>}
        </FormControl>
      </Grid>
    );
  },
);

export default FormSelect;
